import React, { FC } from 'react';
import produce from 'immer';
import { get } from 'lodash';
import { Input, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import merge from '~/utils/mergeWithUndefined';
import { FormItemInjectedProps } from '~/types/FormItemInjectedProps';
import { ApplicationPayload } from '~/types/ApplicationPayload';
import FormDrawer from '~/components/FormDrawer';
import { Form, NationalitySelect } from '~/components';
import { useValidator } from '~/controllers/validation';
import { useGetApplication } from '~/controllers/wizard';
import { useGetDraft } from '~/controllers/draft';
import { Draft } from '~/types/Draft';
import { useOptionalFields } from '~/hooks/useOptionalFields';
import { getStagePayload } from '~/utils/getStagePayload';
import { FormItemContextProvider } from '~/components/Form/FormItemContext';
import { isReadOnly } from '~/utils/isReadOnly';
import InputsGroupLayout from '~/layouts/InputsGroupLayout';
import { KsaShareholders } from '~/types/ksa/KsaShareholders';
import Section from '~/layouts/Section ';
import { stepName } from '~/pages/ksa/application/shareholders';
import { Stage } from '~/types/Application';

interface Props extends Partial<FormItemInjectedProps<KsaShareholders>> {
  uid: string;
  onClose: () => void;
}

const LegalEntityEdit: FC<Props> = (props) => {
  const { uid, onClose } = props;
  const { value, onChange } = props as FormItemInjectedProps<KsaShareholders>;
  const { t } = useTranslation('ksaApplication');
  const shareholdersFormState = { shareholders: value };

  const currentIndex =
    shareholdersFormState.shareholders.legalEntities?.findIndex((item) => item?.uid === uid) || 0;

  const onFinish = (newValues: ApplicationPayload) => {
    const updatedData = merge(shareholdersFormState, newValues);
    onChange?.(updatedData.shareholders);
    onClose();
  };

  return (
    <FormDrawer
      id="legalEntityEditDrawer"
      visible={!!uid}
      onClose={onClose}
      title={t('Legal Entity shareholder')}
      formName={formName}
      withContinueButton={false}
    >
      <LegalEntityForm
        index={currentIndex}
        onFinish={onFinish}
        initialValues={shareholdersFormState}
      />
    </FormDrawer>
  );
};

interface LegalEntityFormProps {
  index: number;
  onFinish: (values: ApplicationPayload) => any;
  initialValues?: any | undefined;
}

const LegalEntityForm: FC<LegalEntityFormProps> = (props) => {
  const { onFinish, initialValues, index } = props;
  const { t } = useTranslation('ksaApplication');

  const [form] = Form.useForm<ApplicationPayload>();
  const getFormState = () => {
    return produce(initialValues || {}, (draft: any) => {
      merge(draft, form.getFieldsValue());
    });
  };
  const validatorController = useValidator(form.getFieldsValue(), undefined, Stage.ksaApplication);
  const applicationQuery = useGetApplication();
  const draftQuery = useGetDraft<Draft<any>>();

  const optionalFieldsController = useOptionalFields({ initialValues });

  const stagePayload = getStagePayload.wizard(applicationQuery.data);

  const nameArr = getLegalEntitiesFormName(index);

  return (
    <FormItemContextProvider
      value={{
        defaultValidator: validatorController.validator,
        applicationPayload: applicationQuery.data?.payload,
        draftPayload: draftQuery.data,
        optionalFields: optionalFieldsController.optionalFields || {},
        errorFields: applicationQuery.data?.payload?.errorInfo?.errors,
        enabledFields: stagePayload?.activeStep.errorInformation?.enabledFields,
        disabledByDefault: isReadOnly(applicationQuery.data),
        disabled: isReadOnly(applicationQuery.data),
      }}
    >
      <Form
        form={form}
        name={formName}
        onFinish={onFinish}
        onValuesChange={(_: any, newValues: any) => {
          optionalFieldsController.debouncedUpdate(newValues);
        }}
        initialValues={initialValues}
      >
        <Section>
          <Typography.Title level={4}>{t('Company license').toUpperCase()}</Typography.Title>
          <Typography.Title level={5}>{t('Document data:')}</Typography.Title>
          <InputsGroupLayout>
            <Form.Item label={t('Company legal name')} name={[...nameArr, 'legalName']} disabled>
              <Input />
            </Form.Item>
            {get(initialValues, [...nameArr, 'commercialRegistrationNumber']) && (
              <Form.Item
                label={t('Commercial Registration number')}
                name={[...nameArr, 'commercialRegistrationNumber']}
                disabled
              >
                <Input />
              </Form.Item>
            )}
            {get(initialValues, [...nameArr, 'entityNumber']) && (
              <Form.Item label={t('Entity number')} name={[...nameArr, 'entityNumber']} disabled>
                <Input />
              </Form.Item>
            )}
            {get(initialValues, [...nameArr, 'nationality']) && (
              <Form.Item
                label={t('Country of incorporation')}
                name={[...nameArr, 'nationality']}
                disabled
              >
                <NationalitySelect />
              </Form.Item>
            )}
          </InputsGroupLayout>
        </Section>
        <Section>
          <Typography.Title level={4}>{t('Ownership share:').toUpperCase()}</Typography.Title>
          <InputsGroupLayout>
            <Form.Item
              label={t('Shares, %')}
              name={[...nameArr, 'ownershipShare', 'percent']}
              disabled
            >
              <Input />
            </Form.Item>
            <Form.Item
              label={t('Shares count')}
              name={[...nameArr, 'ownershipShare', 'count']}
              disabled
            >
              <Input />
            </Form.Item>
          </InputsGroupLayout>
        </Section>
      </Form>
    </FormItemContextProvider>
  );
};

const formName = 'legalEntities';
export const getLegalEntitiesFormName = (index: number) => [stepName, formName, index];

export default LegalEntityEdit;
